import { isAdmin } from '@/constants/roles';
import {
    SHARED_DASHBOARDS_KEY,
    type SharedDashboardSummary,
    exportDashboardAsApp,
    listSharedDashboards,
    setDashboardListing,
    shareDashboard
} from '@/lib/dashboards-api';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import useSWR, { mutate as refreshCache } from 'swr';

import { threadHistoryState, useConfig } from '@chainlit/react-client';

import {
    ArtifactCards,
    ArtifactCardsSkeleton
} from '@/components/Artifacts/ArtifactCards';
import { ArtifactDashboardView } from '@/components/Artifacts/ArtifactDashboardView';
import { ArtifactDetailPanel } from '@/components/Artifacts/ArtifactDetailPanel';
import { DashboardAccessDialog } from '@/components/Artifacts/DashboardAccessDialog';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import { Input } from '@/components/ui/input';

import { useAuth } from '@/hooks/useAuth';

export default function OrganizationArtifacts() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { config } = useConfig();
    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [accessFor, setAccessFor] = useState<SharedDashboardSummary | null>(
        null
    );
    const { user } = useAuth();
    const userIsAdmin = isAdmin(user?.metadata?.organization_roles);

    const enabled = config?.features?.organization_artifacts_enabled === true;

    useEffect(() => {
        setThreadHistory((prev) =>
            prev?.currentThreadId === '__none__'
                ? prev
                : { ...prev, currentThreadId: '__none__' }
        );
    }, [setThreadHistory]);

    const {
        data: dashboards = [],
        error,
        isLoading
    } = useSWR(enabled ? SHARED_DASHBOARDS_KEY : null, listSharedDashboards, {
        revalidateOnFocus: false
    });

    if (config && !enabled) {
        return <Navigate to="/" replace />;
    }

    if (id) {
        return (
            <ArtifactDashboardView
                key={id}
                dashboardId={id}
                backTo="/organization-artifacts"
                backLabel="Organization artifacts"
            />
        );
    }

    const query = searchQuery.trim().toLowerCase();
    const visibleDashboards = query
        ? dashboards.filter(
              (dashboard) =>
                  dashboard.title.toLowerCase().includes(query) ||
                  dashboard.ownerIdentifier.toLowerCase().includes(query)
          )
        : dashboards;
    const selected = dashboards.find(
        (dashboard) => dashboard.id === selectedId
    );

    return (
        <div className="flex size-full">
            <div className="relative flex flex-col flex-1 bg-background min-w-0">
                <MobileNavTrigger />
                <div className="mx-auto px-3 sm:px-12 pt-8 sm:pt-12 pb-6 sm:pb-8 w-full max-w-agentPageWidth">
                    <div className="flex flex-col gap-4 sm:gap-6">
                        <div className="flex flex-col gap-1">
                            <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                                Organization artifacts
                            </h1>
                            <p className="text-muted-foreground text-sm sm:text-lg">
                                Dashboards your colleagues have shared with your
                                organization
                            </p>
                        </div>
                        <div className="relative">
                            <Search className="top-1/2 left-3 absolute size-4 sm:size-5 text-muted-foreground -translate-y-1/2" />
                            <Input
                                type="text"
                                placeholder="Search by name or owner..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 sm:pl-10 h-9 sm:h-10"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex-1 mx-auto px-3 sm:px-12 pt-1 pb-6 sm:pb-8 w-full max-w-agentPageWidth overflow-auto [scrollbar-gutter:stable]">
                    {isLoading ? (
                        <ArtifactCardsSkeleton />
                    ) : error ? (
                        <div className="flex justify-center items-center py-8 text-destructive text-sm">
                            Organization artifacts could not be loaded
                        </div>
                    ) : visibleDashboards.length === 0 ? (
                        <div className="flex justify-center items-center py-8 text-muted-foreground text-sm">
                            {query
                                ? 'No artifacts match your search'
                                : 'Nothing has been shared yet'}
                        </div>
                    ) : (
                        <ArtifactCards
                            dashboards={visibleDashboards}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            canView={(dashboard) =>
                                (dashboard as SharedDashboardSummary).hasAccess
                            }
                            onOpen={(id) =>
                                navigate(`/organization-artifacts/${id}`)
                            }
                            ownerOf={(dashboard) =>
                                (dashboard as SharedDashboardSummary)
                                    .ownerIdentifier
                            }
                            canManage={(dashboard) =>
                                userIsAdmin ||
                                (dashboard as SharedDashboardSummary)
                                    .ownerIdentifier === user?.identifier
                            }
                            onManage={(dashboard) =>
                                setAccessFor(
                                    dashboard as SharedDashboardSummary
                                )
                            }
                        />
                    )}
                </div>

                {accessFor && (
                    <DashboardAccessDialog
                        dashboardId={accessFor.id}
                        title={accessFor.title}
                        open
                        onOpenChange={(open) => !open && setAccessFor(null)}
                    />
                )}
            </div>

            {selected && (
                <ArtifactDetailPanel
                    dashboard={selected}
                    onClose={() => setSelectedId(null)}
                    hasAccess={selected.hasAccess}
                    onOpen={() =>
                        navigate(`/organization-artifacts/${selected.id}`)
                    }
                    onExportToApps={async (name, grouping, access) => {
                        await exportDashboardAsApp(
                            selected.id,
                            name,
                            grouping,
                            access
                        );
                        await refreshCache(['dashboard', selected.id]);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                    }}
                    onToggleListing={async (visible) => {
                        await setDashboardListing(selected.id, visible);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                    }}
                    onToggleShare={async (shared) => {
                        await shareDashboard(selected.id, shared);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                        setSelectedId(null);
                    }}
                />
            )}
        </div>
    );
}
